import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Headline, Icon, Text } from '@oneaudi/unified-web-alpha-components';
import { Headline as HeadlineLegacy, Text as TextLegacy } from '@oneaudi/unified-web-components';
import {
  AudiCode,
  Build,
  DealerAddress,
  DealerDetails,
  DealerText,
  FymDataWrapper,
  MatchScore,
  OpenDays,
  OpenHours,
  OpenTime,
  OpenTimeTable,
  OpenTimeWrapper,
  Title,
  VehicleCode,
  VehicleInfoWrapper,
  VehicleTitle,
} from './VehicleInfo.styles';
import LocationIcon from '../../assets/icons/LocationIcon';
import { DealerLocationData, StockCarTypes, VehicleByAudiCode } from '../../types';
import { SkeletonDealerAddress } from '../Skeleton';
import { selectAppConfig } from '../../redux/appConfigReducer';
import OfferNoVehicle from './Vehicle/OfferNoVehicle';
import OfferSpecificVehicle from './Vehicle/OfferSpecificVehicle';
import VinSpecificVehicle from './Vehicle/VinSpecificVehicle';
import PhoneIcon from '../../assets/icons/PhoneIcon';
import { phoneNumberFormatter } from '../../functions/phoneNumberFormatter';
import TimeIcon from '../../assets/icons/TimeIcon';
import formatOpenHours from '../../functions/formatOpenHours';
import { P, Title as H } from '../Common';

interface VehicleInfoProps {
  dealer?: DealerLocationData;
  vehicle?: StockCarTypes | VehicleByAudiCode;
  isGeneralForm: boolean;
  isLoadingDealer: boolean;
  isVehicleLoading: boolean;
}

const VehicleInfo: React.FC<VehicleInfoProps> = ({
  dealer,
  vehicle,
  isGeneralForm,
  isLoadingDealer,
  isVehicleLoading,
}) => {
  const { t } = useTranslation();
  const { isOfferPage, offers, customDepartmentName, customPhoneNumber, fym, market } =
    useSelector(selectAppConfig);

  const isCreditBonusOffer = offers?.offerType === 'credit';

  const renderVehicleInfo = useMemo(() => {
    if (isOfferPage && isCreditBonusOffer)
      return <OfferNoVehicle offers={offers} isOfferPage={isOfferPage} market={market} />;
    if (isOfferPage && offers)
      return <OfferSpecificVehicle offers={offers} isOfferPage={isOfferPage} market={market} />;
    if (
      vehicle ||
      isVehicleLoading
      // (!isOfferPage && vehicle && presetForm !== 'tradeIn') ||
      // (presetForm === 'tradeIn' && vehicleId)
    )
      return <VinSpecificVehicle isVehicleLoading={isVehicleLoading} vehicle={vehicle} />;

    return null;
  }, [isOfferPage, isCreditBonusOffer, offers, market, vehicle, isVehicleLoading]);

  const dealerAddress = useMemo(() => {
    return { street: dealer?.address?.[0], cityAndZip: dealer?.address?.[1] };
  }, [dealer]);

  const dealerTitle = customDepartmentName ?? t('digitalLeads.inputs.dealerDetails');
  const dealerPhone = customPhoneNumber ? phoneNumberFormatter(customPhoneNumber) : dealer?.phone;

  const initialOpenHours = dealer?.openingHours?.departments[0].openingHours ?? null;

  const openedHours = formatOpenHours(initialOpenHours);
  const openingHoursLabel = t('digitalLeads.openingHours');

  const renderAddress = useMemo(() => {
    if (process.env.IS_LEGACY) {
      return (
        <DealerAddress>
          <LocationIcon />
          {isLoadingDealer ? (
            <SkeletonDealerAddress />
          ) : (
            <DealerText data-testid="dealer-info-address">
              <p>{dealerAddress.street}</p>
              <p>{dealerAddress.cityAndZip}</p>
            </DealerText>
          )}
        </DealerAddress>
      );
    }

    return (
      <DealerAddress>
        <div
          style={{
            color: 'hsla(216, 33%, 99%, 0.7)',
          }}
        >
          <Icon name="poi" size="s" />
        </div>
        {isLoadingDealer ? (
          <SkeletonDealerAddress />
        ) : (
          <Text variant={{ type: 'copy', order: '1', style: 'normal' }} tag="p">
            {dealerAddress.street}
            <br />
            {dealerAddress.cityAndZip}
          </Text>
        )}
      </DealerAddress>
    );
  }, [dealerAddress, isLoadingDealer]);

  const renderPhone = useMemo(() => {
    if (process.env.IS_LEGACY) {
      return (
        <DealerAddress>
          <PhoneIcon />
          {isLoadingDealer ? (
            <SkeletonDealerAddress />
          ) : (
            <DealerText data-testid="dealer-info-phone">
              <p>{dealerPhone}</p>
            </DealerText>
          )}
        </DealerAddress>
      );
    }

    return (
      <DealerAddress>
        <div
          style={{
            color: 'hsla(216, 33%, 99%, 0.7)',
          }}
        >
          <Icon name="voice-call-1" size="s" />
        </div>
        {isLoadingDealer ? (
          <SkeletonDealerAddress />
        ) : (
          <Text variant={{ type: 'copy', order: '1', style: 'normal' }} tag="p">
            {dealerPhone}
          </Text>
        )}
      </DealerAddress>
    );
  }, [dealerPhone, isLoadingDealer]);

  const renderOpeningHours = useMemo(() => {
    if (process.env.IS_LEGACY) {
      return (
        <OpenTimeWrapper>
          <TimeIcon />
          <OpenTimeTable>
            <div>{openingHoursLabel}</div>
            {openedHours &&
              openedHours.map((hours) => {
                return (
                  <OpenTime key={hours.id}>
                    <OpenDays>{hours.id}:</OpenDays>
                    <OpenHours>
                      {hours.open ? `${hours.openTime} - ${hours.closeTime}` : 'Closed'}
                    </OpenHours>
                  </OpenTime>
                );
              })}
          </OpenTimeTable>
        </OpenTimeWrapper>
      );
    }

    return (
      <OpenTimeWrapper>
        <div
          style={{
            color: 'hsla(216, 33%, 99%, 0.7)',
          }}
        >
          <Icon name="time" size="s" />
        </div>
        <OpenTimeTable>
          <Text variant={{ type: 'copy', order: '1', style: 'normal' }} tag="p">
            {openingHoursLabel}
          </Text>
          {openedHours &&
            openedHours.map((hours) => {
              return (
                <Text
                  variant={{ type: 'copy', order: '2', style: 'normal' }}
                  tag="p"
                  key={hours.id}
                >
                  {`${hours.id}: ${hours.open ? `${hours.openTime} - ${hours.closeTime}` : 'Closed'}`}
                </Text>
              );
            })}
        </OpenTimeTable>
      </OpenTimeWrapper>
    );
  }, [openedHours, openingHoursLabel]);

  const renderFYM = useMemo(() => {
    if (!fym) return null;
    if (process.env.IS_LEGACY) {
      return (
        <FymDataWrapper data-testid="fym_data">
          <MatchScore>
            <TextLegacy>{t('digitalLeads.vehicle.fym.matchScore')}</TextLegacy>
            <TextLegacy variant={{ type: 'copy', order: '1', style: 'bold' }} tag="p">
              {fym?.matchScore}%
            </TextLegacy>
          </MatchScore>
          <Build>
            <HeadlineLegacy variant={{ type: 'headline', order: '4', weight: 'normal' }} tag="h4">
              {t('digitalLeads.vehicle.fym.build')}
            </HeadlineLegacy>
            <VehicleTitle>
              <HeadlineLegacy variant={{ type: 'headline', order: '3', weight: 'normal' }} tag="h3">
                {vehicle?.titleText}
              </HeadlineLegacy>
              <TextLegacy variant={{ type: 'copy', order: '1', style: 'normal' }}>
                {vehicle?.subtitleText}
              </TextLegacy>
            </VehicleTitle>
            <VehicleCode>
              <AudiCode>
                <TextLegacy>{t('digitalLeads.vehicle.fym.audiCode')}</TextLegacy>
                <TextLegacy variant={{ type: 'copy', order: '1', style: 'bold' }} tag="p">
                  {fym?.yourBuild?.audiCode}
                </TextLegacy>
              </AudiCode>
              <TextLegacy variant={{ type: 'copy', order: '2', style: 'normal' }} tag="p">
                {t('digitalLeads.vehicle.fym.codeDisclaimer')}
              </TextLegacy>
            </VehicleCode>
          </Build>
        </FymDataWrapper>
      );
    }
    return (
      <FymDataWrapper data-testid="fym_data">
        <MatchScore>
          <P>{t('digitalLeads.vehicle.fym.matchScore')}</P>
          <P textStyle="bold">{fym?.matchScore}%</P>
        </MatchScore>
        <Build>
          <H order="4" tag="h4">
            {t('digitalLeads.vehicle.fym.build')}
          </H>
          <VehicleTitle>
            <H order="3" tag="h3">
              {vehicle?.titleText}
            </H>
            <P>{vehicle?.subtitleText}</P>
          </VehicleTitle>
          <VehicleCode>
            <AudiCode>
              <P>{t('digitalLeads.vehicle.fym.audiCode')}</P>
              <P textStyle="bold">{fym?.yourBuild?.audiCode}</P>
            </AudiCode>
            <P order="2">{t('digitalLeads.vehicle.fym.codeDisclaimer')}</P>
          </VehicleCode>
        </Build>
      </FymDataWrapper>
    );
  }, [fym, t, vehicle?.subtitleText, vehicle?.titleText]);

  return (
    <VehicleInfoWrapper data-testid="vehicle-dealer-info-wrapper">
      {renderVehicleInfo}
      {renderFYM}
      {dealer && (
        <DealerDetails isGeneralForm={isGeneralForm} data-testid="dealer-info">
          {process.env.IS_LEGACY ? (
            <Title>{dealerTitle}</Title>
          ) : (
            <Headline variant={{ type: 'headline', order: '4', weight: 'normal' }} tag="h2">
              {dealerTitle}
            </Headline>
          )}
          {renderAddress}
          {renderPhone}
          {renderOpeningHours}
        </DealerDetails>
      )}
    </VehicleInfoWrapper>
  );
};

export default VehicleInfo;
